/* eslint-disable no-param-reassign */
import { AfterViewChecked, ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, signal } from '@angular/core';
import { MatTabsModule } from '@angular/material/tabs';
import { TabID, WindowStore } from './window.store';
import {CdkMenu, CdkMenuItem, CdkContextMenuTrigger} from '@angular/cdk/menu';
import { I18n, NgtButtonComponent, TranslatePipe } from '@corporate/ng-tebis-base-components';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';

export const globalDraggedTab = signal<{windowIndex: number, tabIndex: number}|null>(null);

@Component({
  selector: 'tps-window-tabs',
  imports: [
    MatTabsModule,
    CdkContextMenuTrigger,
    CdkMenu,
    CdkMenuItem,
    NgtButtonComponent,
    MatIconModule,
    MatMenuModule,
    CdkDropList,
    CdkDrag,
    TranslatePipe,
  ],
  templateUrl: './window-tabs.component.html',
  styleUrl: './window-tabs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {'style': 'overflow: hidden; min-width: 0;'}
})
export class WindowTabsComponent implements AfterViewChecked {

  i18n = inject(I18n);
  elementRef = inject(ElementRef<HTMLElement>);
  windowStore = inject(WindowStore);

  windowIndex = input.required<number>();
  dropListID = input.required<string>();
  connectedDropLists = input<string[]>([]);

  window = computed(() => this.windowStore.windows()[this.windowIndex()]);
  tabs = computed(() => this.window().tabs);
  activeTabIndex = computed(() => this.window().activeTabIndex);

  maxTabsBeforeOverflow = signal(0);
  overflowing = computed(() => this.tabs().length > this.maxTabsBeforeOverflow());
  overflowBreakPoint = computed(() => this.maxTabsBeforeOverflow() - 1);
  overflowedTabSelected = computed(() => this.activeTabIndex() >= this.overflowBreakPoint());
  tabsBeforeOverflow = computed(() => {
    if (this.overflowing()) {
      return this.tabs().slice(0, this.overflowBreakPoint());
    }
    return this.tabs();
  });
  tabsAfterOverflow = computed(() => {
    if (this.overflowing()) {
      return this.tabs().slice(this.overflowBreakPoint());
    }
    return [];
  });

  globalDraggedTab = globalDraggedTab;

  constructor() {
    this.i18n.addNamespace('windowtabs');
  }

  setSelected(tabIndex: number) {
    this.windowStore.selectTabAtIndex(this.windowIndex(), tabIndex);
  }

  setOverflowedSelected(tabIndex: number|string) {
    tabIndex = Number(tabIndex);
    tabIndex += this.overflowBreakPoint();
    this.windowStore.selectTabAtIndex(this.windowIndex(), tabIndex);
  }

  close(tabID: TabID) {
    this.windowStore.closeTab(this.windowIndex(), tabID);
  }

  closeOthers(tabID: TabID) {
    this.windowStore.closeAllTabsExcept(this.windowIndex(), tabID);
  }

  closeLeft(tabID: TabID) {
    this.windowStore.closeTabsLeftOf(this.windowIndex(), tabID);
  }

  closeRight(tabID: TabID) {
    this.windowStore.closeTabsRightOf(this.windowIndex(), tabID);
  }

  getTabUrl(tabID: TabID) {
    return this.windowStore.tabMap()[tabID].url;
  }

  getTabLabel(tabID: TabID) {
    return this.windowStore.tabMap()[tabID].label;
  }

  getTabIcon(tabID: TabID) {
    return this.windowStore.tabMap()[tabID].type;
  }

  // onDrop(event: CdkDragDrop<Tab[]>) {
  onDrop(event: CdkDragDrop<number>) {
    if (event.previousContainer === event.container) {
      this.windowStore.moveTab(this.windowIndex(), event.previousIndex, event.currentIndex);
    } else {
      this.windowStore.transferTab(event.previousContainer.data, event.previousIndex, event.container.data, event.currentIndex);
    }
  }

  ngAfterViewChecked(): void {
    this.maxTabsBeforeOverflow.set(Math.floor(this.elementRef.nativeElement.clientWidth / 200));
  }

  getTooltipForTab(tabID: TabID) {
    const tab = this.windowStore.tabMap()[tabID];
    if (tab.type == 'home' || tab.type == 'none') {
      return `${tab.label()}`;
    }

    let typeName = '';
    switch (tab.type) {
      case 'customer': typeName = this.i18n.translate('customer')(); break;
      case 'order': typeName = this.i18n.translate('manufacturingOrder')(); break;
      case 'article': typeName = this.i18n.translate('article')(); break;
      default: break;
    }
    return `${typeName} ${tab.label()}`;
  }
}
